import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Navigation, Phone, Mail, ShieldCheck, HeartHandshake, AlertTriangle, ExternalLink, ChevronDown, ChevronUp, List, Map as MapIcon, Leaf, Award, Star, Filter, MessageSquare, X, Camera, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import AdCarousel from '../components/AdCarousel';
import PublicLogo from '../components/PublicLogo';
import PublicFeatureNav from '../components/PublicFeatureNav';

const API = process.env.REACT_APP_BACKEND_URL;

const isEmbedded = () => !!localStorage.getItem('token');

// Product type label mapping (mirrors ProductsView DEFAULT_PRODUCT_TYPES)
const PRODUCT_TYPE_LABELS = {
  nuc: 'Nuc', queen: 'Queen', bee_package: 'Bee Package', hive: 'Hive',
  equipment: 'Equipment', wax: 'Wax', propolis: 'Propolis', pollen: 'Pollen',
  honey: 'Honey', other: 'Other'
};
const getProductTypeLabel = (type) => PRODUCT_TYPE_LABELS[type] || type || '';

// Custom honey marker icon
const honeyIcon = new L.DivIcon({
  className: 'custom-honey-marker',
  html: `<div style="width:36px;height:36px;background:#d97706;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:18px;">&#x1F36F;</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

const StarRating = ({ rating, size = 'sm', interactive = false, onChange }) => {
  const sizeClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star
          key={i}
          className={`${sizeClass} ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={interactive ? () => onChange?.(i) : undefined}
        />
      ))}
    </div>
  );
};

const ReviewPhoto = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    fetch(`${API}/api/files/${path}`).then(r => r.blob()).then(b => setSrc(URL.createObjectURL(b))).catch(() => {});
  }, [path]);
  if (!src) return null;
  return <img src={src} alt="Review" className="w-20 h-20 rounded-lg object-cover mt-2 border border-stone-200" />;
};

const ReviewSection = ({ beekeeperId, avgRating, reviewCount }) => {
  const [reviews, setReviews] = useState(null);
  const [showReviews, setShowReviews] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [reviewPhotoPreview, setReviewPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const reviewFileRef = useRef(null);

  const loadReviews = async () => {
    try {
      const res = await fetch(`${API}/api/public/reviews/${beekeeperId}`);
      const data = await res.json();
      setReviews(data.reviews || []);
    } catch { setReviews([]); }
  };

  const toggleReviews = () => {
    if (!reviews && !showReviews) loadReviews();
    setShowReviews(!showReviews);
  };

  const handleSubmit = async () => {
    if (!name.trim() || rating < 1) return;
    setSubmitting(true);
    try {
      let res;
      if (reviewPhoto) {
        const fd = new FormData();
        fd.append('reviewer_name', name);
        fd.append('rating', rating);
        fd.append('comment', comment);
        fd.append('photo', reviewPhoto);
        res = await fetch(`${API}/api/public/reviews/${beekeeperId}/with-photo`, { method: 'POST', body: fd });
      } else {
        res = await fetch(`${API}/api/public/reviews/${beekeeperId}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewer_name: name, rating, comment })
        });
      }
      if (res.ok) {
        setName(''); setRating(0); setComment(''); setShowForm(false);
        setReviewPhoto(null); setReviewPhotoPreview(null);
        setSubmitSuccess(true);
        loadReviews();
      }
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="border-t border-stone-100 bg-stone-50/50 px-5 py-3">
      <div className="flex items-center justify-between">
        <button onClick={toggleReviews} className="flex items-center gap-2 group" data-testid="view-reviews-btn">
          {avgRating > 0 && (
            <>
              <StarRating rating={Math.round(avgRating)} />
              <span className="text-sm font-semibold text-stone-700">{avgRating}</span>
            </>
          )}
          <span className="text-xs text-amber-600 group-hover:text-amber-700 font-medium">
            {reviewCount > 0 ? `${reviewCount} review${reviewCount !== 1 ? 's' : ''}` : 'No reviews yet'}
          </span>
          <ChevronDown className={`h-3.5 w-3.5 text-amber-600 transition-transform ${showReviews ? 'rotate-180' : ''}`} />
        </button>
        <button
          onClick={() => { setShowForm(!showForm); if (!showReviews) toggleReviews(); }}
          className="text-xs font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1"
          data-testid="write-review-btn"
        >
          <MessageSquare className="h-3.5 w-3.5" /> Write a review
        </button>
      </div>

      {submitSuccess && (
        <p className="text-xs text-green-600 bg-green-50 rounded-lg px-3 py-2 mt-2 font-medium">
          Thank you! Your review has been submitted and is pending approval.
        </p>
      )}

      {/* Collapsible content */}
      {showReviews && (
        <div className="mt-3 space-y-2">
          {showForm && (
            <div className="bg-white rounded-xl p-3 border border-stone-200 mb-3 space-y-2" data-testid="review-form">
              <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="h-9 text-sm" data-testid="review-name-input" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-500">Rating:</span>
                <StarRating rating={rating} size="md" interactive onChange={setRating} />
              </div>
              <Textarea placeholder="Share your experience (optional)" value={comment} onChange={e => setComment(e.target.value)} className="text-sm min-h-[60px]" data-testid="review-comment-input" />
              {/* Photo attachment */}
              <div className="flex items-center gap-2">
                {reviewPhotoPreview ? (
                  <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-stone-200">
                    <img src={reviewPhotoPreview} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => { setReviewPhoto(null); setReviewPhotoPreview(null); }} className="absolute top-0 right-0 bg-black/60 rounded-bl-lg p-0.5"><X className="h-3 w-3 text-white" /></button>
                  </div>
                ) : (
                  <button onClick={() => reviewFileRef.current?.click()} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 border border-dashed border-amber-300 rounded-lg px-3 py-2 hover:bg-amber-50 transition-colors">
                    <Camera className="h-3.5 w-3.5" /> Add Photo
                  </button>
                )}
                <input ref={reviewFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) { setReviewPhoto(f); setReviewPhotoPreview(URL.createObjectURL(f)); }
                  e.target.value = '';
                }} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button>
                <Button size="sm" onClick={handleSubmit} disabled={!name.trim() || rating < 1 || submitting} className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8" data-testid="submit-review-btn">
                  {submitting ? 'Sending...' : 'Submit Review'}
                </Button>
              </div>
            </div>
          )}

          {reviews && reviews.length > 0 && (
            <div className="space-y-2">
              {reviews.slice(0, 5).map(r => (
                <div key={r.id} className="bg-white rounded-lg p-2.5 border border-stone-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-800">{r.reviewer_name}</span>
                    <StarRating rating={r.rating} />
                  </div>
                  {r.comment && <p className="text-xs text-stone-600 mt-1">{r.comment}</p>}
                  {r.photo && <ReviewPhoto path={r.photo} />}
                  <p className="text-[10px] text-stone-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
              {reviews.length > 5 && (
                <p className="text-xs text-stone-400 text-center">Showing 5 of {reviews.length} reviews</p>
              )}
            </div>
          )}
          {reviews && reviews.length === 0 && (
            <p className="text-xs text-stone-400 text-center py-2">No reviews yet. Be the first!</p>
          )}
        </div>
      )}
    </div>
  );
};

const BeekeeperCard = ({ bk }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-amber-300 transition-all duration-300 group" data-testid={`beekeeper-card-${bk.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-3">
            {bk.logo ? (
              <img src={`${API}/api/uploads/${bk.logo}`} alt={bk.business_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-stone-200" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-700 font-bold text-sm">
                {bk.business_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-stone-900 group-hover:text-amber-700 transition-colors">{bk.business_name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                {bk.location_text && (
                  <p className="text-sm text-stone-500 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {bk.location_text}
                  </p>
                )}
                {bk.avg_rating > 0 && (
                  <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {bk.avg_rating}
                  </span>
                )}
              </div>
            </div>
          </div>
          {bk.distance_km != null && (
            <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
              {bk.distance_km < 1 ? '< 1 km' : `${bk.distance_km} km`}
            </span>
          )}
        </div>
        
        {bk.bio && <p className="text-sm text-stone-600 mb-3 line-clamp-2">{bk.bio}</p>}
        
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {bk.phone && (
              <a href={`tel:${bk.phone}`} className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-amber-700 font-medium">
                <Phone className="h-4 w-4" /> {bk.phone}
              </a>
            )}
            {bk.email && (
              <a href={`mailto:${bk.email}`} className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-amber-700 font-medium">
                <Mail className="h-4 w-4" /> Email
              </a>
            )}
            {!bk.phone && !bk.email && (
              <span className="text-xs text-stone-400">Contact info not shared</span>
            )}
          </div>
          {bk.products?.length > 0 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700"
            >
              {bk.products.length} product{bk.products.length !== 1 ? 's' : ''}
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
      
      {/* Expandable Products Section */}
      {expanded && bk.products?.length > 0 && (
        <div className="border-t border-stone-100 bg-stone-50/50 p-4">
          <div className="grid grid-cols-1 gap-2">
            {bk.products.map((p, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-2.5 border border-stone-100">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 text-lg">
                    {p.product_type === 'honey' ? '\uD83C\uDF6F' : p.product_type === 'queen' ? '\uD83D\uDC51' : p.product_type === 'wax' ? '\uD83D\uDD6F\uFE0F' : '\uD83D\uDCE6'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 text-sm">{p.name}</p>
                  {p.description && <p className="text-xs text-stone-600 line-clamp-2 mt-0.5">{p.description}</p>}
                  <div className="flex items-center gap-2 mt-0.5">
                    {getProductTypeLabel(p.product_type) && (
                      <span className="text-xs text-amber-600 font-medium">{getProductTypeLabel(p.product_type)}</span>
                    )}
                    {p.stock_quantity != null && (
                      <span className="text-xs text-stone-400">· QTY: {p.stock_quantity}</span>
                    )}
                  </div>
                </div>
                {p.price && (
                  <span className="font-bold text-amber-700 text-sm whitespace-nowrap">
                    ${p.price}{p.unit ? `/${p.unit}` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews Section */}
      <ReviewSection beekeeperId={bk.id} avgRating={bk.avg_rating || 0} reviewCount={bk.review_count || 0} />
    </div>
  );
};

const EducationCard = ({ article }) => (
  <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 group">
    {article.image_url && (
      <div className="h-48 overflow-hidden">
        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
    )}
    <div className="p-5">
      <span className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2 block">{article.category}</span>
      <h3 className="text-lg font-bold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">{article.title}</h3>
      <p className="text-sm text-stone-600 line-clamp-3 mb-3">{article.summary}</p>
      {article.external_link && (
        <a href={article.external_link} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
          Read more <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      {article.video_url && (
        <a href={article.video_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1 mt-1">
          Watch video <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  </div>
);


// Notify Me Card — shown when no listings found (full) or below results (compact)
const NotifyMeCard = ({ compact = false, userLocation = '', userLat, userLng }) => {
  const [form, setForm] = useState({ name: '', email: '', mobile: '', location: userLocation });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(!compact);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email && !form.mobile) { setError('Please provide an email or mobile number'); return; }
    if (!form.location) { setError('Please enter your suburb or city'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/honey-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lat: userLat || null, lng: userLng || null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={`${compact ? 'mt-8' : 'py-12'}`}>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center max-w-lg mx-auto">
          <Bell className="h-10 w-10 text-green-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-green-800 mb-1">You're on the list!</h3>
          <p className="text-sm text-green-700">We'll let you know when a local honey supplier becomes available in your area.</p>
        </div>
      </div>
    );
  }

  if (compact && !showForm) {
    return (
      <div className="mt-8 text-center">
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-100 text-amber-800 font-semibold text-sm hover:bg-amber-200 transition-colors" data-testid="notify-me-toggle">
          <Bell className="h-4 w-4" /> Can't find honey near you? Get notified
        </button>
      </div>
    );
  }

  return (
    <div className={`${compact ? 'mt-8' : 'py-8'}`} data-testid="notify-me-section">
      <div className={`bg-amber-50 border border-amber-200 rounded-2xl p-6 sm:p-8 max-w-lg mx-auto ${!compact ? 'shadow-sm' : ''}`}>
        {!compact && (
          <div className="text-center mb-5">
            <Bell className="h-10 w-10 text-amber-500 mx-auto mb-2" />
            <h3 className="text-xl font-bold text-stone-800 mb-1">No beekeepers found yet</h3>
            <p className="text-sm text-stone-600">Leave your details and we'll notify you when a local honey supplier becomes available in your area.</p>
          </div>
        )}
        {compact && (
          <div className="text-center mb-4">
            <h3 className="text-base font-bold text-amber-800 flex items-center justify-center gap-2"><Bell className="h-4 w-4" /> Get notified about new suppliers</h3>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input placeholder="Your name (optional)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white" data-testid="notify-name" />
          <Input placeholder="Email address" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white" data-testid="notify-email" />
          <Input placeholder="Mobile number" type="tel" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} className="bg-white" data-testid="notify-mobile" />
          <Input placeholder="Your suburb or city *" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="bg-white" required data-testid="notify-location" />
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <Button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl h-11" data-testid="notify-submit">
            {submitting ? 'Submitting...' : 'Notify Me'}
          </Button>
          <p className="text-[10px] text-stone-400 text-center">We'll only contact you about honey availability in your area.</p>
        </form>
      </div>
    </div>
  );
};


const FindHoney = () => {
  const [beekeepers, setBeekeepers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [radiusKm, setRadiusKm] = useState(50);
  const [viewMode, setViewMode] = useState('list');
  const [locationStatus, setLocationStatus] = useState('');
  const [mapCenter, setMapCenter] = useState([-27.47, 153.02]);
  const [activeTypeFilter, setActiveTypeFilter] = useState('');
  const resultsRef = useRef(null);

  const fetchBeekeepers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userLat && userLng) {
        params.set('lat', userLat);
        params.set('lng', userLng);
        params.set('radius_km', radiusKm);
      }
      if (submittedSearch) params.set('search', submittedSearch);
      const res = await fetch(`${API}/api/public/find-honey?${params}`);
      const data = await res.json();
      const bks = data.beekeepers || [];
      setBeekeepers(bks);
      // Auto-center map on first beekeeper with location (if no user location set)
      if (!userLat && !userLng) {
        const withLoc = bks.find(b => b.has_location && b.lat && b.lng);
        if (withLoc) setMapCenter([withLoc.lat, withLoc.lng]);
      }
      // Scroll to results only after explicit search submission
      if (submittedSearch && resultsRef.current) {
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userLat, userLng, radiusKm, submittedSearch]);

  useEffect(() => {
    fetchBeekeepers();
  }, [fetchBeekeepers]);

  useEffect(() => {
    fetch(`${API}/api/public/education-content`)
      .then(r => r.json())
      .then(data => setArticles(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('Geolocation not supported');
      return;
    }
    setLocationStatus('Locating...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setMapCenter([pos.coords.latitude, pos.coords.longitude]);
        setLocationStatus('');
      },
      () => setLocationStatus('Location access denied'),
      { timeout: 10000 }
    );
  };

  // Compute available product types across all beekeepers
  const availableTypes = [...new Set(beekeepers.flatMap(b => (b.products || []).map(p => p.product_type).filter(Boolean)))];

  // Filter beekeepers by selected product type
  const filteredBeekeepers = activeTypeFilter
    ? beekeepers.filter(b => b.products?.some(p => p.product_type === activeTypeFilter))
    : beekeepers;
  const beekeepersWithLocation = filteredBeekeepers.filter(b => b.has_location);

  const embedded = isEmbedded();

  return (
    <div className={`min-h-screen honeycomb-bg ${embedded ? '' : ''}`}>
      {/* Sticky Header — public only */}
      {!embedded && (
      <header className="backdrop-blur-xl bg-white/80 border-b border-amber-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <PublicLogo size="header" />
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-stone-600 hover:text-amber-700 text-sm font-medium">Log In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl px-5" data-testid="join-thrive-header-btn">
                Join Thrive
              </Button>
            </Link>
          </div>
        </div>
      </header>
      )}

      {/* Educational Section — public only */}
      {!embedded && articles.length > 0 && (
        <section className="bg-stone-100/60 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900 mb-3">
                Why Buy Local Honey?
              </h2>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                Learn about the importance of supporting backyard beekeepers and the truth about honey fraud.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => <EducationCard key={article.id} article={article} />)}
            </div>
          </div>
        </section>
      )}

      {/* Static Educational Fallback (when no admin articles exist) — public only */}
      {!embedded && articles.length === 0 && (
        <section className="bg-stone-100/60 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900 mb-3">
                Why Buy Local Honey?
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">Stop Honey Fraud</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Up to 76% of supermarket honey is adulterated with cheap syrups. Buying directly from local beekeepers guarantees you get the real thing — pure, raw, and unprocessed.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <HeartHandshake className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">Support Small Beekeepers</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Backyard beekeepers face rising costs, bee health challenges, and competition from mass-produced imports. Your purchase directly supports local families and sustainable beekeeping.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <ShieldCheck className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">Health Benefits</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Raw local honey contains beneficial enzymes, pollen, and antioxidants that are destroyed in commercial processing. Many people find local honey helps with seasonal allergies.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-b border-amber-100">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-20 md:pb-24">
          <div className="max-w-2xl">
          <div className="flex items-start gap-6 mb-4">
            <div className="flex-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-stone-900">
                Find <span className="text-amber-600">Local Honey</span><br />Near You
              </h1>
            </div>
            <PublicLogo size="hero" className="hidden sm:block flex-shrink-0 mt-2" />
          </div>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-xl">
              Support backyard beekeepers in your area. Buy pure, raw, local honey straight from the people who care for the bees.
            </p>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-2xl p-4 border border-stone-200 shadow-sm max-w-2xl">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSubmittedSearch(searchText); }}
                  placeholder="Search honey, beeswax, pollen..."
                  className="pl-10 h-12 bg-stone-50 text-stone-900 border-stone-200 rounded-xl"
                  data-testid="location-search-input"
                />
              </div>
              <Button
                onClick={handleUseMyLocation}
                variant="outline"
                className="h-12 border-stone-300 text-stone-700 hover:bg-stone-50 rounded-xl gap-2"
                data-testid="use-location-btn"
              >
                <Navigation className="h-4 w-4" />
                {locationStatus || 'Near Me'}
              </Button>
              <Button
                onClick={() => setSubmittedSearch(searchText)}
                className="h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl px-6"
                data-testid="search-honey-button"
              >
                Search
              </Button>
            </div>
            {userLat && (
              <div className="flex items-center gap-3 mt-3 text-sm text-stone-600">
                <span>Radius:</span>
                <input
                  type="range" min="5" max="200" value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  className="flex-1 accent-amber-600"
                />
                <span className="font-semibold text-amber-700 min-w-[3rem]">{radiusKm} km</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section ref={resultsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-stone-900">
              {loading ? 'Searching...' : `${filteredBeekeepers.length} Local Beekeeper${filteredBeekeepers.length !== 1 ? 's' : ''} Found`}
            </h2>
            {userLat && <p className="text-sm text-stone-500">Within {radiusKm} km of your location</p>}
          </div>
          <div className="flex bg-stone-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
            >
              <List className="h-4 w-4" /> List
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'map' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
            >
              <MapIcon className="h-4 w-4" /> Map
            </button>
          </div>
        </div>

        {/* Product Type Filter Chips */}
        {availableTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="product-type-filters">
            <Filter className="h-4 w-4 text-stone-400" />
            <button
              onClick={() => setActiveTypeFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!activeTypeFilter ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
              data-testid="filter-all"
            >
              All
            </button>
            {availableTypes.map(type => (
              <button
                key={type}
                onClick={() => setActiveTypeFilter(activeTypeFilter === type ? '' : type)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeTypeFilter === type ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                data-testid={`filter-${type}`}
              >
                {getProductTypeLabel(type)}
              </button>
            ))}
          </div>
        )}

        {viewMode === 'map' && beekeepersWithLocation.length > 0 ? (
          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm mb-8" style={{ height: '500px' }}>
            <MapContainer key={`${mapCenter[0]}-${mapCenter[1]}`} center={mapCenter} zoom={userLat ? 10 : 10} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {beekeepersWithLocation.map(bk => (
                <Marker key={bk.id} position={[bk.lat, bk.lng]} icon={honeyIcon}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <strong className="text-amber-700">{bk.business_name}</strong>
                      {bk.distance_km != null && <span className="text-xs ml-2">({bk.distance_km} km)</span>}
                      {bk.bio && <p className="text-xs text-stone-500 mt-1">{bk.bio}</p>}
                      {bk.products?.length > 0 && (
                        <p className="text-xs mt-1 font-medium">{[...new Set(bk.products.map(p => getProductTypeLabel(p.product_type)).filter(Boolean))].join(', ')}</p>
                      )}
                      {bk.phone && <p className="text-xs mt-1"><a href={`tel:${bk.phone}`} className="text-amber-600">{bk.phone}</a></p>}
                      {bk.email && <p className="text-xs"><a href={`mailto:${bk.email}`} className="text-amber-600">Email</a></p>}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : viewMode === 'map' ? (
          <div className="bg-stone-100 rounded-2xl p-12 text-center mb-8">
            <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No beekeepers with map locations found. Try expanding your search radius or switch to list view.</p>
          </div>
        ) : null}

        {/* Sponsored Listing */}
        <div className="mb-5">
          <AdCarousel placement="find_honey_sponsored" />
        </div>

        {/* Beekeeper Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBeekeepers.map(bk => <BeekeeperCard key={bk.id} bk={bk} />)}
        </div>

        {!loading && filteredBeekeepers.length === 0 && (
          <NotifyMeCard userLocation={locationStatus === 'Located!' ? `${userLat}, ${userLng}` : ''} userLat={userLat} userLng={userLng} />
        )}

        {/* Notify Me CTA — always visible below results */}
        {!loading && filteredBeekeepers.length > 0 && (
          <NotifyMeCard compact userLocation={locationStatus === 'Located!' ? `${userLat}, ${userLng}` : ''} userLat={userLat} userLng={userLng} />
        )}
      </section>


      {/* CTA Section — public only */}
      {!embedded && (
      <section className="bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-lg">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                Are you a <span className="text-amber-400">Beekeeper</span>?
              </h2>
              <p className="text-stone-400 leading-relaxed">
                Join Thrive to manage your hives, track inspections, and get discovered by local honey buyers. 
                List your products for free and connect directly with your community.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl px-8 h-12 text-base" data-testid="join-thrive-cta-button">
                  <Award className="h-5 w-5 mr-2" /> Join Thrive — It's Free
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-800 rounded-xl px-6 h-12">
                  Already a member? Log in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer — public only */}
      {!embedded && (
      <footer className="bg-stone-800 text-stone-400 text-center py-6 text-sm space-y-2">
        <p>&copy; {new Date().getFullYear()} Thrive Beekeeping. Supporting backyard beekeepers worldwide.</p>
        <p className="text-stone-500">Are you a supplier? <a href="/advertise" className="text-amber-400 hover:text-amber-300 underline">Advertise on Thrive</a> — reach thousands of beekeepers.</p>
      </footer>
      )}
      {!embedded && <PublicFeatureNav current="/find-honey" />}
    </div>
  );
};

export default FindHoney;
